/* 다운로드(사진으로 저장) — 페이지를 브라우저 그리기 영역에 직접 그려 JPG로. 1080×1440(3:4)
   · 모든 페이지: 페이지마다 한 장씩 ZIP 하나 / 지금 페이지만: JPG 한 장 / 긴 이미지: 모든 페이지를 세로로 이은 한 장
   화면(render.tsx)과 같은 규칙으로 그린다 — 사진 cover 자르기·모양, 도형·선, 글(줄바꿈·정렬·자간·줄간격·배경). */
import { DEFAULT_TEXT_COLOR, PHOTO_CLIPS, SHAPES } from './constants';
import { loadImage } from './images';
import { PAGE_H, PAGE_W, type El, type Page } from './types';

const BASE_SCALE = 1.5;
type Ctx = CanvasRenderingContext2D & { letterSpacing?: string };

/* ── 색: 단색 또는 linear-gradient(180deg, 색 위치, …) ── */
function splitTop(s: string) {
  const out: string[] = [];
  let d = 0, cur = '';
  for (const ch of s) {
    if (ch === '(') d++;
    if (ch === ')') d--;
    if (ch === ',' && !d) { out.push(cur.trim()); cur = ''; } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}
function fillOf(g: Ctx, bg: string, w: number, h: number): string | CanvasGradient {
  const m = /^linear-gradient\((.*)\)$/i.exec(bg.trim());
  if (!m) return bg;
  const parts = splitTop(m[1]);
  let deg = 180;
  if (/deg$/.test(parts[0])) deg = parseFloat(parts.shift()!);
  else if (/^to /.test(parts[0])) { const t = parts.shift()!; deg = /bottom/.test(t) ? 180 : /top/.test(t) ? 0 : /right/.test(t) ? 90 : 270; }
  const rad = ((deg - 90) * Math.PI) / 180, len = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad));
  const cx = w / 2, cy = h / 2, dx = (Math.cos(rad) * len) / 2, dy = (Math.sin(rad) * len) / 2;
  const grad = g.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
  parts.forEach((p, i) => {
    const mm = /^(.*?)(?:\s+([\d.]+)%)?$/.exec(p)!;
    grad.addColorStop(mm[2] != null ? +mm[2] / 100 : i / Math.max(1, parts.length - 1), mm[1]);
  });
  return grad;
}
function roundRect(g: Ctx, x: number, y: number, w: number, h: number, r: number | number[]) {
  g.beginPath();
  if (typeof g.roundRect === 'function') g.roundRect(x, y, w, h, r as number);
  else g.rect(x, y, w, h);
}

/* ── 글 줄바꿈 — 한글은 글자마다, 영어·숫자는 낱말 단위로 끊는다(브라우저와 비슷하게) ── */
function wrapText(g: Ctx, text: string, maxW: number) {
  const lines: string[] = [];
  text.split('\n').forEach((para) => {
    const tokens = para.match(/[A-Za-z0-9.,!?'"()\-:/%&]+|\s+|./gu) || [''];
    let line = '';
    tokens.forEach((t) => {
      const next = line + t;
      if (g.measureText(next).width <= maxW || !line) { line = next; return; }
      if (/^\s+$/.test(t)) { lines.push(line); line = ''; return; }
      lines.push(line.replace(/\s+$/, ''));
      line = t;
      // 한 낱말이 한 줄보다 길면 글자 단위로
      while (g.measureText(line).width > maxW && line.length > 1) {
        let k = line.length - 1;
        while (k > 1 && g.measureText(line.slice(0, k)).width > maxW) k--;
        lines.push(line.slice(0, k));
        line = line.slice(k);
      }
    });
    lines.push(line);
  });
  return lines;
}
function padOf(elm: El) {
  const p = elm.bg ? elm.pad || '8px 14px' : '4px';
  const n = p.split(/\s+/).map((v) => parseFloat(v) || 0);
  const [t, r = t, b = t, l = r] = n;
  return { t, r, b, l };
}
function drawText(g: Ctx, elm: El) {
  const fs = elm.fontSize || 16, lh = fs * (parseFloat(String(elm.lh || 1.45)) || 1.45);
  if (elm.bg) { g.fillStyle = fillOf(g, elm.bg, elm.w, elm.h); roundRect(g, 0, 0, elm.w, elm.h, elm.radius || 0); g.fill(); }
  const pad = padOf(elm);
  g.save();
  roundRect(g, 0, 0, elm.w, elm.h, 0);
  g.clip();
  g.font = `${elm.italic ? 'italic ' : ''}${elm.bold ? 800 : 500} ${fs}px ${elm.font || "'Pretendard GOV','Pretendard','Malgun Gothic',sans-serif"}`;
  if ('letterSpacing' in g) g.letterSpacing = elm.ls ? (/em$/.test(elm.ls) ? `${parseFloat(elm.ls) * fs}px` : elm.ls) : '0px';
  g.fillStyle = elm.color || DEFAULT_TEXT_COLOR;
  g.textBaseline = 'middle';
  const innerW = elm.w - pad.l - pad.r;
  const lines = wrapText(g, elm.text || '', innerW);
  const total = lines.length * lh;
  let y = elm.vcenter ? (elm.h - total) / 2 : pad.t;
  lines.forEach((ln) => {
    const w = g.measureText(ln).width;
    const x = elm.align === 'center' ? pad.l + (innerW - w) / 2 : elm.align === 'right' ? elm.w - pad.r - w : pad.l;
    g.fillText(ln, x, y + lh / 2);
    if (elm.underline) g.fillRect(x, y + lh / 2 + fs * 0.55, w, Math.max(1, fs / 14));
    y += lh;
  });
  g.restore();
}

function clipImage(g: Ctx, elm: El) {
  const { w, h } = elm;
  const clip = elm.clip && elm.clip !== 'none' ? elm.clip : null;
  if (clip === 'circle') { g.beginPath(); g.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); }
  else if (clip === 'arch') { const r = Math.min(w / 2, h); g.beginPath(); g.moveTo(0, h); g.lineTo(0, r); g.ellipse(w / 2, r, w / 2, r, 0, Math.PI, 0); g.lineTo(w, h); g.closePath(); }
  else if (clip && PHOTO_CLIPS[clip]?.poly) { g.beginPath(); PHOTO_CLIPS[clip].poly!.forEach(([x, y], i) => (i ? g.lineTo(x * w, y * h) : g.moveTo(x * w, y * h))); g.closePath(); }
  else roundRect(g, 0, 0, w, h, clip === 'round' ? elm.radius || 28 : Math.min(elm.radius || 0, w / 2, h / 2));
  g.clip();
}
async function drawImageEl(g: Ctx, elm: El) {
  g.save();
  clipImage(g, elm);
  g.fillStyle = '#D1D5DB'; // dq-ignore: 사진이 없을 때 회색 칸 (문서 그림)
  g.fillRect(0, 0, elm.w, elm.h);
  if (elm.src) {
    try {
      const im = await loadImage(elm.src, false);
      const s = Math.max(elm.w / im.naturalWidth, elm.h / im.naturalHeight);
      const dw = im.naturalWidth * s, dh = im.naturalHeight * s;
      g.drawImage(im, (elm.w - dw) / 2, (elm.h - dh) / 2, dw, dh);
    } catch { /* 회색 칸 그대로 */ }
  }
  g.restore();
}
function drawShape(g: Ctx, elm: El) {
  const kind = elm.kind || 'rect', { w, h } = elm;
  if (kind === 'line') {
    const sw = elm.sw || 3, c = elm.stroke || elm.bg || '#1F2937', y = h / 2, head = Math.max(10, sw * 3.2), a = elm.arrow || 'none'; // dq-ignore: 사진으로 내보낼 때 그리는 문서 기본 색 — render.tsx 와 같아야 함
    const x1 = a === 'both' ? head : sw / 2, x2 = a === 'end' || a === 'both' ? w - head : w - sw / 2;
    g.strokeStyle = c; g.fillStyle = c; g.lineWidth = sw;
    g.setLineDash(elm.dash === 'dashed' ? [sw * 3, sw * 2] : elm.dash === 'dotted' ? [0.01, sw * 2] : []);
    g.lineCap = elm.dash === 'dotted' ? 'round' : 'butt';
    g.beginPath(); g.moveTo(x1, y); g.lineTo(x2, y); g.stroke();
    g.setLineDash([]);
    const tri = (x: number, dir: number) => { g.beginPath(); g.moveTo(x, y - head * 0.6); g.lineTo(x + dir * head, y); g.lineTo(x, y + head * 0.6); g.closePath(); g.fill(); };
    if (a === 'end' || a === 'both') tri(w - head, 1);
    if (a === 'both') tri(head, -1);
    return;
  }
  const bg = elm.bg || 'transparent';
  if (kind === 'rect' || kind === 'round' || kind === 'circle') {
    if (kind === 'circle') { g.beginPath(); g.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); }
    else roundRect(g, 0, 0, w, h, Math.min(kind === 'round' ? elm.radius ?? 24 : elm.radius || 0, w / 2, h / 2));
    g.fillStyle = fillOf(g, bg, w, h); g.fill();
    if (elm.sw) { g.save(); g.clip(); g.strokeStyle = elm.stroke || '#111827'; g.lineWidth = elm.sw * 2; g.stroke(); g.restore(); } // dq-ignore: 사진으로 내보낼 때 그리는 문서 기본 색 — render.tsx 와 같아야 함
    return;
  }
  const d = SHAPES[kind]?.d;
  if (!d) return;
  const p = new Path2D();
  p.addPath(new Path2D(d), new DOMMatrix().scale(w / 100, h / 100));
  g.fillStyle = fillOf(g, bg, w, h); g.fill(p);
  if (elm.sw) { g.strokeStyle = elm.stroke || '#111827'; g.lineWidth = elm.sw; g.lineJoin = 'round'; g.stroke(p); } // dq-ignore: 사진으로 내보낼 때 그리는 문서 기본 색 — render.tsx 와 같아야 함
}

async function drawPage(g: Ctx, page: Page) {
  g.fillStyle = fillOf(g, page.bg || '#FFFFFF', PAGE_W, PAGE_H); // dq-ignore: 사진으로 내보낼 때 그리는 문서 기본 색 — render.tsx 와 같아야 함
  g.fillRect(0, 0, PAGE_W, PAGE_H);
  for (const elm of page.elements.slice().sort((a, b) => a.z - b.z)) {
    g.save();
    g.translate(elm.x + elm.w / 2, elm.y + elm.h / 2);
    if (elm.rot) g.rotate((elm.rot * Math.PI) / 180);
    g.translate(-elm.w / 2, -elm.h / 2);
    if (elm.opacity != null && elm.opacity < 1) g.globalAlpha = elm.opacity;
    if (elm.flipX || elm.flipY) { g.translate(elm.flipX ? elm.w : 0, elm.flipY ? elm.h : 0); g.scale(elm.flipX ? -1 : 1, elm.flipY ? -1 : 1); }
    if (elm.type === 'image') await drawImageEl(g, elm);
    else if (elm.type === 'shape') drawShape(g, elm);
    else drawText(g, elm);
    g.restore();
  }
}
async function fontsReady(pages: Page[]) {
  const fams = new Set<string>();
  pages.forEach((p) => p.elements.forEach((e) => { if (e.type === 'text') fams.add(`${e.bold ? 800 : 500} 16px ${e.font || "'Pretendard GOV'"}`); }));
  await Promise.all([...fams].map((f) => document.fonts.load(f).catch(() => undefined)));
  await document.fonts.ready;
}
type Fmt = 'jpeg' | 'png';
const toBlob = (c: HTMLCanvasElement, fmt: Fmt = 'jpeg') => new Promise<Blob>((res, rej) =>
  c.toBlob((b) => (b ? res(b) : rej(new Error('사진을 만들지 못했어요'))), fmt === 'png' ? 'image/png' : 'image/jpeg', fmt === 'png' ? undefined : 0.92));

/** 시공 사진 원본을 PNG로(블로그용 — 화질 그대로). 긴 변이 너무 크면(3000px 넘으면) 3000px로 줄인다 */
const PHOTO_MAX = 3000;
export async function photoToPng(src: string): Promise<Blob> {
  const im = await loadImage(src, false);
  const k = Math.min(1, PHOTO_MAX / Math.max(im.naturalWidth, im.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(im.naturalWidth * k); c.height = Math.round(im.naturalHeight * k);
  const g = c.getContext('2d') as Ctx;
  g.imageSmoothingQuality = 'high';
  g.drawImage(im, 0, 0, c.width, c.height);
  return toBlob(c, 'png');
}

/** 블로그용 사진 칸 — 원본을 템플릿 칸(w×h) 비율로 가운데를 잘라 JPG로(화면의 cover 와 같은 자르기). 긴 변 1600px까지 */
const FRAME_MAX = 1600;
export async function photoFrameJpeg(src: string, w: number, h: number): Promise<Blob> {
  const im = await loadImage(src, false);
  const ar = w / Math.max(1, h);
  let sw = im.naturalWidth, sh = sw / ar;
  if (sh > im.naturalHeight) { sh = im.naturalHeight; sw = sh * ar; }
  const k = Math.min(1, FRAME_MAX / Math.max(sw, sh));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * k)); c.height = Math.max(1, Math.round(sh * k));
  const g = c.getContext('2d') as Ctx;
  g.imageSmoothingQuality = 'high';
  g.drawImage(im, (im.naturalWidth - sw) / 2, (im.naturalHeight - sh) / 2, sw, sh, 0, 0, c.width, c.height);
  return toBlob(c, 'jpeg');
}

/** opt.format·scale — 블로그용은 PNG·2배(1440×1920), 다운로드는 JPG·1.5배(1080×1440) */
export async function renderPages(pages: Page[], mode: 'all' | 'one' | 'long', opt: { format?: Fmt; scale?: number } = {}): Promise<{ name: string; blob: Blob }[]> {
  const SCALE = opt.scale ?? BASE_SCALE, fmt = opt.format ?? 'jpeg';
  await fontsReady(pages);
  if (mode === 'long') {
    const c = document.createElement('canvas');
    c.width = PAGE_W * SCALE; c.height = PAGE_H * SCALE * pages.length;
    const g = c.getContext('2d') as Ctx;
    for (let i = 0; i < pages.length; i++) {
      g.save(); g.scale(SCALE, SCALE); g.translate(0, PAGE_H * i);
      await drawPage(g, pages[i]);
      g.restore();
    }
    return [{ name: 'long', blob: await toBlob(c, fmt) }];
  }
  const out: { name: string; blob: Blob }[] = [];
  for (let i = 0; i < pages.length; i++) {
    const c = document.createElement('canvas');
    c.width = PAGE_W * SCALE; c.height = PAGE_H * SCALE;
    const g = c.getContext('2d') as Ctx;
    g.scale(SCALE, SCALE);
    await drawPage(g, pages[i]);
    out.push({ name: String(i + 1).padStart(2, '0'), blob: await toBlob(c, fmt) });
  }
  return out;
}

/* ── 압축하지 않는(저장 방식) ZIP — JPG는 이미 압축돼 있어서 그대로 담는다 ── */
const CRC = Array.from({ length: 256 }, (_, n) => { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; return c >>> 0; });
function crc32(b: Uint8Array) { let c = 0xffffffff; for (let i = 0; i < b.length; i++) c = CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; }
export async function makeZip(files: { name: string; blob: Blob }[]): Promise<Blob> {
  const enc = new TextEncoder(), parts: BlobPart[] = [], central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const data = new Uint8Array(await f.blob.arrayBuffer()), name = enc.encode(f.name), crc = crc32(data), size = data.length;
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); local.setUint16(4, 20, true); local.setUint16(6, 0x0800, true); // UTF-8 파일명
    local.setUint32(14, crc, true); local.setUint32(18, size, true); local.setUint32(22, size, true); local.setUint16(26, name.length, true);
    parts.push(local.buffer, name as BlobPart, data as BlobPart);
    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true); cen.setUint16(4, 20, true); cen.setUint16(6, 20, true); cen.setUint16(8, 0x0800, true);
    cen.setUint32(16, crc, true); cen.setUint32(20, size, true); cen.setUint32(24, size, true); cen.setUint16(28, name.length, true); cen.setUint32(42, offset, true);
    central.push(new Uint8Array(cen.buffer), name);
    offset += 30 + name.length + size;
  }
  const cenSize = central.reduce((n, b) => n + b.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, cenSize, true); end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end.buffer] as BlobPart[], { type: 'application/zip' });
}
